import { AgentTaskStatus, type AgentTask } from "@forgepoint/shared";
import { CircleDot } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useStandardAgentDisplayName } from "@/modules/standardAgent/useStandardAgentDisplayName";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import { ThreadTaskDescription } from "./ThreadTaskDescription";
import { ThreadTaskDescriptionPreview } from "./ThreadTaskDescriptionPreview";

interface ThreadTaskRowProps {
  task: AgentTask;
  currentUserId?: string;
  compact?: boolean;
  dependencyLabel?: string;
  onTaskClick: (task: AgentTask) => void;
  selectedTaskId?: string | null;
  descriptionExpanded: boolean;
  onDescriptionToggle: (task: AgentTask) => void;
}

/** Opens task details and shows shared assignments without changing task status. */
export function ThreadTaskRow({
  task,
  currentUserId,
  compact = false,
  dependencyLabel,
  onTaskClick,
  selectedTaskId,
  descriptionExpanded,
  onDescriptionToggle,
}: ThreadTaskRowProps) {
  const agentName = useStandardAgentDisplayName();
  const owners = task.owners.map((owner) => {
    if (owner.type === "agent") {
      return { label: agentName, detail: agentName };
    }
    const name = [owner.firstName, owner.lastName].filter(Boolean).join(" ");
    let label = owner.firstName || owner.email || "User";
    if (owner.id === currentUserId) {
      label = "You";
    }
    return {
      label,
      detail: [name, owner.email].filter(Boolean).join(" · "),
    };
  });
  const isOnlyYou =
    task.owners.length === 1 &&
    task.owners[0].type === "userId" &&
    task.owners[0].id === currentUserId;
  const showOwners = !isOnlyYou && owners.length > 0;
  const isAssignedToYou =
    task.status === AgentTaskStatus.IN_PROGRESS &&
    task.owners.some(
      (owner) => owner.type === "userId" && owner.id === currentUserId,
    );
  const description = task.description?.trim();
  const showPreview =
    Boolean(description) && task.status === AgentTaskStatus.IN_PROGRESS;
  return (
    <Collapsible
      open={descriptionExpanded}
      onOpenChange={() => onDescriptionToggle(task)}
    >
      <div
        className={cn(
          "relative isolate flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-muted/60",
          selectedTaskId === task.id && "bg-muted/60",
        )}
      >
        <CircleDot
          className={cn(
            "h-3.5 w-3.5 shrink-0",
            isAssignedToYou
              ? "text-orange-500 dark:text-orange-400"
              : "text-muted-foreground",
          )}
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <button
            type="button"
            onClick={() => onTaskClick(task)}
            className="flex w-full min-w-0 items-center gap-2 text-left after:absolute after:inset-0 after:rounded-md hover:text-muted-foreground focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-ring"
          >
            <span
              className="min-w-0 flex-1 truncate text-sm leading-5"
              title={task.subject ?? "Untitled task"}
            >
              {task.subject ?? "Untitled task"}
            </span>
          </button>
          <div className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
            {showOwners && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label={`Assignees: ${owners.map((owner) => owner.label).join(", ")}`}
                    className="relative z-10 min-w-0 truncate rounded-sm text-left text-xs text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {owners
                      .slice(0, 2)
                      .map((owner) => owner.label)
                      .join(", ")}
                    {owners.length > 2 && (
                      <span className="underline decoration-dotted underline-offset-2">
                        {" "}
                        and {owners.length - 2} more
                      </span>
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  {owners.map((owner, index) => (
                    <div key={index}>{owner.detail}</div>
                  ))}
                </TooltipContent>
              </Tooltip>
            )}
            {!compact && task.owners.length === 0 && (
              <p className="shrink-0">Unassigned</p>
            )}
            {!compact &&
              task.status === AgentTaskStatus.PENDING &&
              dependencyLabel && (
                <p className="truncate" title={dependencyLabel}>
                  {dependencyLabel}
                </p>
              )}
          </div>
          {showPreview && description && (
            <ThreadTaskDescriptionPreview
              description={description}
              subject={task.subject ?? "Untitled task"}
              expanded={descriptionExpanded}
            />
          )}
        </div>
      </div>
      {description && (
        <CollapsibleContent>
          <ThreadTaskDescription description={description} />
        </CollapsibleContent>
      )}
    </Collapsible>
  );
}
